import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';
import { COLORS } from '../../../../shared/colors';
import { SPRINT_MANAGEMENT_CONSTANTS } from '../../../../shared/appConstants';

const { UTILISATION_STATUS } = SPRINT_MANAGEMENT_CONSTANTS;

export const getLoadThresholds = (avgSp) => {
  const average = Math.max(0, Number(avgSp) || 0);
  return {
    average,
    lower: average * 0.7,
    upper: average * 1.2,
  };
};

export const getUtilisation = (sp, avgSp) => {
  const value = Number(sp) || 0;
  const thresholds = getLoadThresholds(avgSp);
  if (value > thresholds.upper) return UTILISATION_STATUS.OVER;
  if (value >= thresholds.lower) return UTILISATION_STATUS.OPTIMAL;
  return UTILISATION_STATUS.UNDER;
};

const getBarColor = (sp, avgSp) => {
  const utilisation = getUtilisation(sp, avgSp);
  if (utilisation === UTILISATION_STATUS.OVER) return COLORS.charts.loadRed;
  if (utilisation === UTILISATION_STATUS.OPTIMAL) return COLORS.charts.loadOrange;
  return COLORS.charts.loadBlue;
};

const CustomTooltip = ({ active, payload, avgSp }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const sp = data.sp || 0;
    const valueColor = getBarColor(sp, avgSp);
    const utilisation = getUtilisation(sp, avgSp);
    const average = getLoadThresholds(avgSp).average;
    const statusText = utilisation === UTILISATION_STATUS.OVER 
      ? t('sprintManagement.statusOverUtilised') 
      : utilisation === UTILISATION_STATUS.UNDER 
        ? t('sprintManagement.statusUnderUtilised') 
        : t('sprintManagement.statusOptimal');
        
    const difference = Math.abs(sp - average);
    const capacityText = utilisation === UTILISATION_STATUS.OVER
      ? t('sprintManagement.capacityExceeded', { sp: difference })
      : utilisation === UTILISATION_STATUS.UNDER
        ? t('sprintManagement.capacityShort', { sp: difference })
        : t('sprintManagement.capacityOptimal');

    return (
      <div className="chart-custom-tooltip">
        <p className="tooltip-label" title={data.name}>{data.name}</p>
        
        <div className="tooltip-row">
          <span className="tooltip-key">{t('sprintManagement.assigned')}</span>
          <span className="tooltip-val" style={{ color: valueColor }}>{sp} SP</span>
        </div>
        
        <div className="tooltip-row">
          <span className="tooltip-key">{t('sprintManagement.status')}</span>
          <span className="tooltip-val" style={{ color: valueColor }}>{statusText}</span>
        </div>
        
        <div className="tooltip-row">
          <span className="tooltip-key">{t('sprintManagement.tickets')}</span>
          <span className="tooltip-val" style={{ color: valueColor }}>{data.tickets}</span>
        </div>
        
        <div className="tooltip-row last-row">
          <span className="tooltip-key">{t('sprintManagement.capacity')}</span>
          <span className="tooltip-val" style={{ color: valueColor }}>{capacityText}</span>
        </div>
      </div>
    );
  }
  return null;
};

const CustomLegend = (props) => {
  const { payload } = props;
  return (
    <div className="chart-legend">
      {payload.map((entry, index) => (
        <div key={`item-${index}`} className="legend-item">
          <span className="legend-color-dot" style={{ backgroundColor: entry.color }}></span>
          {entry.value}
        </div>
      ))}
    </div>
  );
};

const CustomXAxisTick = ({ x, y, payload, isTransparent }) => {
  const name = payload.value || '';
  const truncated = name.length > 12 ? name.substring(0, 10) + '...' : name;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill={isTransparent ? COLORS.transparent : COLORS.gray[500]} fontSize={12}>
        {!isTransparent && <title>{name}</title>}
        {truncated}
      </text>
    </g>
  );
};

const SprintLoadChart = ({ sprintId, apiData }) => {
  const { t } = useTranslation();

  const teamLoad = apiData?.data?.team_load || {};
  const loadData = teamLoad.data || [];
  const avgSp = Number(teamLoad.avg_sp) || 0;
  const thresholds = getLoadThresholds(avgSp);
  const optimalMin = Math.ceil(thresholds.lower);
  const optimalMax = Math.floor(thresholds.upper);

  const customLegendPayload = [
    { value: t('sprintManagement.legendOverUtilised', { max: optimalMax }), type: 'circle', id: 'ID01', color: COLORS.charts.loadRed },
    { value: t('sprintManagement.legendOptimal', { min: optimalMin, max: optimalMax }), type: 'circle', id: 'ID02', color: COLORS.charts.loadOrange },
    { value: t('sprintManagement.legendUnderUtilised', { min: optimalMin }), type: 'circle', id: 'ID03', color: COLORS.charts.loadBlue },
  ];

  // Calculate width to support horizontal scrolling if many members
  const minChartWidth = Math.max(100, loadData.length * 120);

  return (
    <div className="sm-card chart-card sprint-load-card report-export-chart-resized">
      <div className="chart-header">
        <h2 className="card-title">{t('sprintManagement.sprintLoadTitle')}</h2>
        <CustomLegend payload={customLegendPayload} />
      </div>
      
      <div className="chart-scroll-container">
        {loadData.length === 0 ? (
          <div className="chart-container">
            <div className="no-data-msg">
              {t('sprintManagement.noDataAvailable', { defaultValue: 'No data available to display' })}
            </div>
          </div>
        ) : (
          <div className="sprint-load-chart-wrapper" style={{ minWidth: `${minChartWidth}px` }}>
            <div className="sprint-load-chart-inner">
              {/* Background Chart: Grid and Reference Lines */}
              <div className="sprint-load-bg-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={loadData} margin={{ top: 40, right: 30, left: 10, bottom: 5 }} barSize={45}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.gray[200]} />
                    <ReferenceLine y={thresholds.lower} stroke={COLORS.charts.loadBlue} strokeDasharray="6 4" />
                    <ReferenceLine y={thresholds.upper} stroke={COLORS.charts.loadOrange} strokeDasharray="6 4" />
                    <XAxis dataKey="name" tick={<CustomXAxisTick />} interval={0} axisLine={{ stroke: COLORS.gray[300] }} tickLine={false} />
                    <YAxis tick={{ fill: COLORS.gray[500], fontSize: 12 }} axisLine={{ stroke: COLORS.gray[300] }} tickLine={false} label={{ value: 'Story Points', position: 'top', offset: 25, fill: COLORS.gray[500], fontSize: 12, fontWeight: 500 }} />
                    {/* Dummy bar to force Recharts to render the chart and axes */}
                    <Bar dataKey="sp" fill="transparent" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Foreground Chart: Bars and Tooltip */}
              <div className="sprint-load-fg-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={loadData} margin={{ top: 40, right: 30, left: 10, bottom: 5 }} barSize={45}>
                    {/* Invisible axes to perfectly align the chart bounds with the background chart */}
                    <XAxis dataKey="name" tick={<CustomXAxisTick isTransparent />} interval={0} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: COLORS.transparent, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip avgSp={avgSp} />} cursor={{ fill: COLORS.ui.shadowLight }} />
                    <Bar dataKey="sp" radius={[4, 4, 0, 0]} minPointSize={4}>
                      <LabelList dataKey="sp" position="top" formatter={(val) => `${val} SP`} style={{ fill: COLORS.gray[500], fontSize: 12, fontWeight: 'bold' }} />
                      {loadData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.sp, avgSp)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SprintLoadChart;
